import axios from 'axios';
import {
  WeatherData,
  WeatherLocation,
  GeocodingResponse,
  ProcessedWeatherData,
  WeatherApiError,
  CurrentWeather,
  HourlyWeather,
  DailyWeather
} from '@/types/weather';
import { getWeatherCondition, getWindDirection } from './weather-codes';

const OPEN_METEO_BASE_URL = 'https://api.open-meteo.com/v1';
const GEOCODING_BASE_URL = 'https://geocoding-api.open-meteo.com/v1';

// API timeout in milliseconds
const API_TIMEOUT = 10000;

// Rate limiting
let lastApiCall = 0;
const MIN_CALL_INTERVAL = 5000; // 5 seconds between calls
const requestCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 300000; // 5 minutes

// Create axios instance with default config for external APIs (no CSRF token)
const apiClient = axios.create({
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});
// Remove CSRF and X-Requested-With headers if inherited from global axios
['X-CSRF-TOKEN', 'X-Requested-With'].forEach((header) => {
  if (apiClient.defaults.headers.common[header]) {
    delete apiClient.defaults.headers.common[header];
  }
});

// Rate limiting helper
const checkRateLimit = (): void => {
  const now = Date.now();
  const timeSinceLastCall = now - lastApiCall;

  if (timeSinceLastCall < MIN_CALL_INTERVAL) {
    const waitTime = MIN_CALL_INTERVAL - timeSinceLastCall;
    throw new Error(`Please wait ${Math.ceil(waitTime / 1000)} seconds before making another request.`);
  }

  lastApiCall = now;
};

// Cache helper
const getCachedData = (key: string): any | null => {
  const cached = requestCache.get(key);
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key: string, data: any): void => {
  requestCache.set(key, { data, timestamp: Date.now() });
};

export class WeatherAPI {
  /**
   * Search for locations by name (offline database)
   */
  static async searchLocations(query: string, limit: number = 10): Promise<WeatherLocation[]> {
    try {
      const cacheKey = `search_${query}_${limit}`;
      const cached = getCachedData(cacheKey);
      if (cached) {
        return cached;
      }

      // Offline city database
      const cities = this.getCityDatabase();

      // Filter cities based on query
      const filteredCities = cities
        .filter(city =>
          city.city.toLowerCase().includes(query.toLowerCase()) ||
          city.country.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, limit);

      setCachedData(cacheKey, filteredCities);
      return filteredCities;
    } catch (error) {
      console.error('Error searching locations:', error);
      return [];
    }
  }

  /**
   * Get offline city database
   */
  private static getCityDatabase(): WeatherLocation[] {
    return [
      // Indonesian major cities
      { latitude: -6.2088, longitude: 106.8456, city: 'Jakarta', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta', elevation: 8 },
      { latitude: -6.9175, longitude: 107.6191, city: 'Bandung', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta', elevation: 768 },
      { latitude: -7.2575, longitude: 112.7521, city: 'Surabaya', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta', elevation: 3 },
      { latitude: 3.5952, longitude: 98.6722, city: 'Medan', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta', elevation: 26 },
      { latitude: -6.9667, longitude: 110.4167, city: 'Semarang', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta', elevation: 3 },
      { latitude: -5.1477, longitude: 119.4327, city: 'Makassar', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Makassar', elevation: 8 },
      { latitude: -2.9761, longitude: 104.7754, city: 'Palembang', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta', elevation: 8 },
      { latitude: -7.7956, longitude: 110.3695, city: 'Yogyakarta', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta', elevation: 113 },
      { latitude: -8.6500, longitude: 115.2167, city: 'Denpasar', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Makassar', elevation: 4 },
      { latitude: -0.7893, longitude: 113.9213, city: 'Pontianak', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Pontianak', elevation: 3 },
      { latitude: -3.3194, longitude: 114.5906, city: 'Banjarmasin', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Makassar', elevation: 6 },
      { latitude: 1.4748, longitude: 124.8421, city: 'Manado', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Makassar', elevation: 79 },

      // Some international cities for reference
      { latitude: 40.7128, longitude: -74.0060, city: 'New York', country: 'United States', countryCode: 'US', timezone: 'America/New_York', elevation: 10 },
      { latitude: 51.5074, longitude: -0.1278, city: 'London', country: 'United Kingdom', countryCode: 'GB', timezone: 'Europe/London', elevation: 11 },
      { latitude: 35.6762, longitude: 139.6503, city: 'Tokyo', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo', elevation: 40 },
      { latitude: -33.8688, longitude: 151.2093, city: 'Sydney', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Sydney', elevation: 58 },
      { latitude: 48.8566, longitude: 2.3522, city: 'Paris', country: 'France', countryCode: 'FR', timezone: 'Europe/Paris', elevation: 35 },
    ];
  }

  /**
   * Get current weather data for a location with retry logic
   */
  static async getCurrentWeather(latitude: number, longitude: number, retryCount = 0): Promise<ProcessedWeatherData> {
    const maxRetries = 2;

    try {
      const cacheKey = `weather_${latitude.toFixed(2)}_${longitude.toFixed(2)}`;
      const cached = getCachedData(cacheKey);
      if (cached) {
        return cached;
      }

      // Skip rate limiting on retries for better user experience
      if (retryCount === 0) {
        checkRateLimit();
      }

      const response = await apiClient.get(`${OPEN_METEO_BASE_URL}/forecast`, {
        params: {
          latitude,
          longitude,
          current: [
            'temperature_2m',
            'relative_humidity_2m',
            'apparent_temperature',
            'is_day',
            'precipitation',
            'weather_code',
            'cloud_cover',
            'surface_pressure',
            'wind_speed_10m',
            'wind_direction_10m',
            'uv_index'
          ].join(','),
          hourly: [
            'temperature_2m',
            'relative_humidity_2m',
            'weather_code',
            'wind_speed_10m',
            'wind_direction_10m',
            'precipitation',
            'is_day'
          ].join(','),
          daily: [
            'weather_code',
            'temperature_2m_max',
            'temperature_2m_min',
            'sunrise',
            'sunset',
            'precipitation_sum',
            'wind_speed_10m_max',
            'wind_direction_10m_dominant',
            'uv_index_max'
          ].join(','),
          timezone: 'auto',
          forecast_days: 7
        },
        timeout: 15000 // Increase timeout for weather data
      });

      const processedData = this.processWeatherData(response.data);
      setCachedData(cacheKey, processedData);
      return processedData;
    } catch (error) {
      console.error(`Error fetching weather data (attempt ${retryCount + 1}):`, error);

      // Retry logic
      if (retryCount < maxRetries) {
        console.log(`Retrying weather fetch in ${(retryCount + 1) * 2} seconds...`);
        await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 2000));
        return this.getCurrentWeather(latitude, longitude, retryCount + 1);
      }

      throw new Error(`Failed to fetch weather data after ${maxRetries + 1} attempts. Please check your internet connection.`);
    }
  }

  /**
   * Get weather forecast for a location
   */
  static async getForecast(
    latitude: number,
    longitude: number,
    days: number = 7
  ): Promise<ProcessedWeatherData> {
    try {
      const response = await apiClient.get(`${OPEN_METEO_BASE_URL}/forecast`, {
        params: {
          latitude,
          longitude,
          current: [
            'temperature_2m',
            'relative_humidity_2m',
            'apparent_temperature',
            'is_day',
            'precipitation',
            'weather_code',
            'cloud_cover',
            'surface_pressure',
            'wind_speed_10m',
            'wind_direction_10m',
            'uv_index'
          ].join(','),
          hourly: [
            'temperature_2m',
            'relative_humidity_2m',
            'weather_code',
            'wind_speed_10m',
            'wind_direction_10m',
            'precipitation',
            'is_day',
            'cloud_cover',
            'surface_pressure',
            'uv_index'
          ].join(','),
          daily: [
            'weather_code',
            'temperature_2m_max',
            'temperature_2m_min',
            'sunrise',
            'sunset',
            'precipitation_sum',
            'wind_speed_10m_max',
            'wind_direction_10m_dominant',
            'uv_index_max'
          ].join(','),
          timezone: 'auto',
          forecast_days: Math.min(days, 16) // Open-Meteo supports up to 16 days
        }
      });

      return this.processWeatherData(response.data);
    } catch (error) {
      console.error('Error fetching forecast data:', error);
      throw new Error('Failed to fetch forecast data');
    }
  }

  /**
   * Get historical weather data
   */
  static async getHistoricalWeather(
    latitude: number,
    longitude: number,
    startDate: string,
    endDate: string
  ): Promise<any> {
    try {
      const response = await apiClient.get(`${OPEN_METEO_BASE_URL}/historical`, {
        params: {
          latitude,
          longitude,
          start_date: startDate,
          end_date: endDate,
          daily: [
            'weather_code',
            'temperature_2m_max',
            'temperature_2m_min',
            'precipitation_sum',
            'wind_speed_10m_max',
            'wind_direction_10m_dominant'
          ].join(','),
          timezone: 'auto'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching historical data:', error);
      throw new Error('Failed to fetch historical weather data');
    }
  }

  /**
   * Get location info from coordinates (simplified - no API call)
   */
  static async getLocationFromCoordinates(
    latitude: number,
    longitude: number
  ): Promise<WeatherLocation | null> {
    try {
      // Simple city detection based on coordinates without API calls
      const cityName = this.getCityFromCoordinates(latitude, longitude);

      return {
        latitude,
        longitude,
        city: cityName,
        country: this.getCountryFromCoordinates(latitude, longitude),
        countryCode: this.getCountryCodeFromCoordinates(latitude, longitude),
        timezone: this.getTimezoneFromCoordinates(latitude, longitude),
        elevation: 0
      };
    } catch (error) {
      console.error('Error getting location from coordinates:', error);
      return null;
    }
  }

  /**
   * Simple city detection based on coordinates (offline)
   */
  private static getCityFromCoordinates(lat: number, lng: number): string {
    // Major Indonesian cities
    const indonesianCities = [
      { name: 'Jakarta', lat: -6.2088, lng: 106.8456, radius: 0.5 },
      { name: 'Bandung', lat: -6.9175, lng: 107.6191, radius: 0.3 },
      { name: 'Surabaya', lat: -7.2575, lng: 112.7521, radius: 0.3 },
      { name: 'Medan', lat: 3.5952, lng: 98.6722, radius: 0.3 },
      { name: 'Semarang', lat: -6.9667, lng: 110.4167, radius: 0.3 },
      { name: 'Makassar', lat: -5.1477, lng: 119.4327, radius: 0.3 },
      { name: 'Palembang', lat: -2.9761, lng: 104.7754, radius: 0.3 },
      { name: 'Yogyakarta', lat: -7.7956, lng: 110.3695, radius: 0.2 },
      { name: 'Denpasar', lat: -8.6500, lng: 115.2167, radius: 0.2 },
    ];

    // Check Indonesian cities first
    for (const city of indonesianCities) {
      const distance = Math.sqrt(
        Math.pow(lat - city.lat, 2) + Math.pow(lng - city.lng, 2)
      );
      if (distance <= city.radius) {
        return city.name;
      }
    }

    // Fallback to region-based detection
    if (lat >= -11 && lat <= 6 && lng >= 95 && lng <= 141) {
      return 'Indonesia'; // Somewhere in Indonesia
    }

    return 'Unknown Location';
  }

  /**
   * Get country from coordinates (simplified)
   */
  private static getCountryFromCoordinates(lat: number, lng: number): string {
    if (lat >= -11 && lat <= 6 && lng >= 95 && lng <= 141) {
      return 'Indonesia';
    }
    return 'Unknown';
  }

  /**
   * Get country code from coordinates (simplified)
   */
  private static getCountryCodeFromCoordinates(lat: number, lng: number): string {
    if (lat >= -11 && lat <= 6 && lng >= 95 && lng <= 141) {
      return 'ID';
    }
    return 'XX';
  }

  /**
   * Get timezone from coordinates (simplified)
   */
  private static getTimezoneFromCoordinates(lat: number, lng: number): string {
    if (lat >= -11 && lat <= 6 && lng >= 95 && lng <= 141) {
      return 'Asia/Jakarta';
    }
    return 'UTC';
  }

  /**
   * Get user's current location using browser geolocation with fallbacks
   */
  static async getCurrentLocation(): Promise<{ latitude: number; longitude: number }> {
    // Fallback locations for major cities (prioritize Indonesian cities)
    const fallbackLocations = [
      { name: 'Jakarta', latitude: -6.2088, longitude: 106.8456 },
      { name: 'Bandung', latitude: -6.9175, longitude: 107.6191 },
      { name: 'Surabaya', latitude: -7.2575, longitude: 112.7521 },
      { name: 'Medan', latitude: 3.5952, longitude: 98.6722 },
      { name: 'Semarang', latitude: -6.9667, longitude: 110.4167 },
    ];

    // Simplified location detection based on timezone (no external API)
    const getTimezoneBasedLocation = (): { latitude: number; longitude: number } => {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Indonesian timezones
      if (timeZone.includes('Jakarta') || timeZone.includes('Indonesia')) {
        return { latitude: -6.2088, longitude: 106.8456 }; // Jakarta
      }

      // Other Asian timezones
      if (timeZone.includes('Asia')) {
        if (timeZone.includes('Singapore')) {
          return { latitude: 1.3521, longitude: 103.8198 }; // Singapore (close to Indonesia)
        }
        if (timeZone.includes('Kuala_Lumpur')) {
          return { latitude: 3.1390, longitude: 101.6869 }; // Kuala Lumpur (close to Indonesia)
        }
        if (timeZone.includes('Tokyo')) {
          return { latitude: 35.6762, longitude: 139.6503 }; // Tokyo
        }
      }

      // Default to Jakarta for any unrecognized timezone
      return { latitude: -6.2088, longitude: 106.8456 };
    };

    // Try browser geolocation first
    const getBrowserLocation = (): Promise<{ latitude: number; longitude: number }> => {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }

        let resolved = false;
        const timeoutId = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            reject(new Error('Browser location timeout'));
          }
        }, 8000); // Shorter timeout for browser geolocation

        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeoutId);
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              });
            }
          },
          (error) => {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeoutId);
              reject(new Error(`Geolocation error: ${error.message}`));
            }
          },
          {
            enableHighAccuracy: false,
            timeout: 6000,
            maximumAge: 300000 // 5 minutes cache
          }
        );
      });
    };

    try {
      // First attempt: Browser geolocation
      return await getBrowserLocation();
    } catch (browserError) {
      console.warn('Browser geolocation failed:', browserError);

      // Second attempt: Timezone-based location detection
      try {
        const timezoneLocation = getTimezoneBasedLocation();
        console.log('Using timezone-based location detection');
        return timezoneLocation;
      } catch (timezoneError) {
        console.warn('Timezone-based location failed:', timezoneError);
      }

      // Final fallback: Use a default location based on user's likely timezone
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let defaultLocation = fallbackLocations[0]; // Default to Jakarta

      // Simple timezone-based location detection
      if (timeZone.includes('Europe')) {
        defaultLocation = fallbackLocations.find(loc => loc.name === 'London') || defaultLocation;
      } else if (timeZone.includes('Asia')) {
        // Prefer Jakarta for Indonesian/Southeast Asian timezones, Tokyo for East Asian
        if (timeZone.includes('Jakarta') || timeZone.includes('Indonesia') || timeZone.includes('Singapore') || timeZone.includes('Kuala_Lumpur')) {
          defaultLocation = fallbackLocations.find(loc => loc.name === 'Jakarta') || defaultLocation;
        } else {
          defaultLocation = fallbackLocations.find(loc => loc.name === 'Tokyo') || defaultLocation;
        }
      } else if (timeZone.includes('Australia')) {
        defaultLocation = fallbackLocations.find(loc => loc.name === 'Sydney') || defaultLocation;
      }

      console.log(`Using fallback location: ${defaultLocation.name} (Jakarta default)`);
      return {
        latitude: defaultLocation.latitude,
        longitude: defaultLocation.longitude
      };
    }
  }

  /**
   * Process raw weather data from Open-Meteo API
   */
  private static processWeatherData(data: any): ProcessedWeatherData {
    const location: WeatherLocation = {
      latitude: data.latitude,
      longitude: data.longitude,
      city: 'Unknown',
      country: data.country,
      countryCode: data.countryCode,
      timezone: data.timezone || 'UTC',
      elevation: data.elevation
    };

    // Process current weather
    const currentCondition = getWeatherCondition(
      data.current.weather_code,
      data.current.is_day === 1
    );

    const current = {
      temperature: Math.round(data.current.temperature_2m),
      condition: {
        ...currentCondition,
        code: data.current.weather_code
      },
      humidity: data.current.relative_humidity_2m,
      windSpeed: Math.round(data.current.wind_speed_10m),
      windDirection: getWindDirection(data.current.wind_direction_10m),
      feelsLike: Math.round(data.current.apparent_temperature),
      pressure: Math.round(data.current.surface_pressure),
      visibility: 10, // Open-Meteo doesn't provide visibility, default to 10km
      uvIndex: data.current.uv_index || 0,
      isDay: data.current.is_day === 1,
      time: new Date(data.current.time)
    };

    // Process hourly forecast (next 24 hours)
    const hourlyForecast = data.hourly.time.slice(0, 24).map((time: string, index: number) => {
      const hourlyCondition = getWeatherCondition(
        data.hourly.weather_code[index],
        data.hourly.is_day[index] === 1
      );

      return {
        time: new Date(time),
        temperature: Math.round(data.hourly.temperature_2m[index]),
        condition: {
          ...hourlyCondition,
          code: data.hourly.weather_code[index]
        },
        precipitation: data.hourly.precipitation[index] || 0,
        windSpeed: Math.round(data.hourly.wind_speed_10m[index])
      };
    });

    // Process daily forecast
    const dailyForecast = data.daily.time.map((date: string, index: number) => {
      const dailyCondition = getWeatherCondition(data.daily.weather_code[index], true);

      return {
        date: new Date(date),
        condition: {
          ...dailyCondition,
          code: data.daily.weather_code[index]
        },
        temperatureMax: Math.round(data.daily.temperature_2m_max[index]),
        temperatureMin: Math.round(data.daily.temperature_2m_min[index]),
        sunrise: new Date(data.daily.sunrise[index]),
        sunset: new Date(data.daily.sunset[index]),
        precipitation: data.daily.precipitation_sum[index] || 0,
        windSpeed: Math.round(data.daily.wind_speed_10m_max[index]),
        uvIndex: data.daily.uv_index_max[index] || 0
      };
    });

    return {
      location,
      current,
      hourlyForecast,
      dailyForecast
    };
  }

  /**
   * Check if the API is available
   */
  static async checkApiStatus(): Promise<boolean> {
    try {
      const response = await apiClient.get(`${OPEN_METEO_BASE_URL}/forecast`, {
        params: {
          latitude: 52.52,
          longitude: 13.41,
          current: 'temperature_2m'
        },
        timeout: 5000
      });
      return response.status === 200;
    } catch (error) {
      console.error('API status check failed:', error);
      return false;
    }
  }
}

export default WeatherAPI;
